<template>
	<!-- Validation error tooltip - controlled by state only -->
	<v-tooltip
		:value="attemptedNegative"
		top
		color="error"
		:open-on-hover="false"
		:disabled="!attemptedNegative"
	>
		<template v-slot:activator="{ attrs }">
			<!-- Regular tooltip for general help text -->
			<v-tooltip
				top
				:disabled="!tooltip"
				:open-delay="300"
			>
				<template v-slot:activator="{ on, attrs: tooltipAttrs }">
					<v-text-field
						class="price-input"
						:class="{
							'manual-override': isManualOverride,
							'negative-amount': isNegative
						}"
						:disabled="disabled"
						prepend-inner-icon="mdi-currency-usd"
						outlined
						dense
						hide-details="auto"
						:error-messages="errorMessages"
						v-model="displayValue"
						@focus="handleFocus"
						@blur="handleBlur"
						v-bind="{ ...attrs, ...tooltipAttrs }"
						v-on="on"
					></v-text-field>
				</template>
				<span>{{ tooltip }}</span>
			</v-tooltip>
		</template>
		<span>
			<v-icon small dark class="mr-1">mdi-alert-circle</v-icon>
			Negative values are only allowed for discount/credit item types
		</span>
	</v-tooltip>
</template>

<script>
export default {
	name: "CurrencyTextField",
	props: {
		value: [Number, String],
		disabled: Boolean,
		isManualOverride: {
			type: Boolean,
			default: false
		},
		allowNegative: {
			type: Boolean,
			default: false
		},
		treatAsCredit: {
			type: Boolean,
			default: false
		},
		tooltip: {
			type: String,
			default: ""
		},
		errorMessages: {
			type: [String, Array],
			default: () => []
		}
	},
	data() {
		return {
			isInputActive: false,
			attemptedNegative: false,
		};
	},
	computed: {
		isNegative() {
			return typeof this.value === 'number' && this.value < 0;
		},
		displayValue: {
			get() {
				if (this.isInputActive) {
					//Input field is focused. unformat display value for user
					if (typeof this.value === "number") {
						// For credit fields, show absolute value when focused (user doesn't need to see minus sign)
						if (this.treatAsCredit) {
							return Math.abs(this.value).toString();
						}
						// If negative values are not allowed, always show absolute value
						if (!this.allowNegative && this.value < 0) {
							return Math.abs(this.value).toString();
						}
						return this.value.toString();
					}
					return null;
				} else {
					//On Blur format display value for user interface
					if (typeof this.value === "number") {
						// Force positive if negatives not allowed
						const displayVal = (!this.allowNegative && !this.treatAsCredit && this.value < 0)
							? Math.abs(this.value)
							: this.value;

						const absValue = Math.abs(displayVal);
						const formatted = absValue
							.toFixed(2)
							.replace(/(\d)(?=(\d{3})+(?:\.\d+)?$)/g, "$1,");

						// Use minus sign for negative amounts
						return displayVal < 0 ? `-${formatted}` : formatted;
					}
					return null;
				}
			},
			set(modifiedValue) {
				// Handle empty values
				if (!modifiedValue || modifiedValue.length === 0) {
					this.attemptedNegative = false;
					// Continue processing to emit 0
				}

				// Skip validation for credit fields (they're always negative)
				if (!this.treatAsCredit && this.isInputActive) {
					// Detect if user is trying to enter a negative value while typing
					const hasNegativeSign = modifiedValue && modifiedValue.includes('-');

					// Show warning tooltip if user attempts negative when not allowed
					if (!this.allowNegative && hasNegativeSign) {
						this.attemptedNegative = true;
					} else {
						this.attemptedNegative = false;
					}
				}

				// Recalculate value after ignoring "$" and "," in user input
				// Allow negative sign if allowNegative prop is true or treatAsCredit is true
				const regex = (this.allowNegative || this.treatAsCredit) ? /[^\d.-]/g : /[^\d.]/g;
				let cleanValue = modifiedValue.replace(regex, "");

				// If user just typed "-" and hasn't entered digits yet, don't emit — let them keep typing
				if (this.isInputActive && cleanValue === '-') {
					return;
				}

				let newValue = parseFloat(cleanValue);
				// Ensure that it is not NaN
				if (isNaN(newValue)) {
					newValue = 0;
				}

				// If treatAsCredit is true, always convert to negative (absolute value then negate)
				if (this.treatAsCredit && newValue !== 0) {
					newValue = -Math.abs(newValue);
				}
				// If negative values are not allowed, always convert to positive (absolute value)
				else if (!this.allowNegative && newValue !== 0) {
					newValue = Math.abs(newValue);
				}

				// Note: we cannot set this.value as it is a "prop". It needs to be passed to parent component
				// $emit the event so that parent component gets it
				this.$emit("input", newValue);
			},
		},
	},
	watch: {
		// Auto-correct negative values that come from parent (not during user typing)
		value: {
			handler(newVal, oldVal) {
				// Only auto-correct when NOT actively typing to avoid interfering with decimal input
				// This catches negative values that come from parent component or external sources
				if (!this.isInputActive && !this.allowNegative && !this.treatAsCredit && typeof newVal === 'number' && newVal < 0) {
					this.$nextTick(() => {
						this.$emit("input", Math.abs(newVal));
					});
				}

				// Clear error state when value changes programmatically (not via user input)
				if (!this.isInputActive && this.attemptedNegative) {
					this.attemptedNegative = false;
				}
			},
			immediate: false
		},
		// Clear error when allowNegative prop changes
		allowNegative(newVal) {
			if (newVal && this.attemptedNegative) {
				this.attemptedNegative = false;
			}

			// Auto-correct negative value when allowNegative changes to false
			if (!newVal && !this.treatAsCredit && typeof this.value === 'number' && this.value < 0) {
				this.$emit("input", Math.abs(this.value));
			}
		},
		// Clear error and auto-correct when treatAsCredit changes
		treatAsCredit(newVal) {
			if (this.attemptedNegative) {
				this.attemptedNegative = false;
			}

			// If changing from credit to non-credit and negatives not allowed, force positive
			if (!newVal && !this.allowNegative && typeof this.value === 'number' && this.value < 0) {
				this.$emit("input", Math.abs(this.value));
			}
		}
	},
	methods: {
		handleFocus(event) {
			this.isInputActive = true;
			// Clear error when user focuses to give them another chance
			this.attemptedNegative = false;

			// Auto-correct negative values when field is focused
			// Mirror treatAsCredit pattern: if not allowed, force positive
			if (!this.allowNegative && !this.treatAsCredit && typeof this.value === 'number' && this.value < 0) {
				this.$emit("input", Math.abs(this.value));
			}

			// Auto-select value on focus so typing immediately replaces it
			this.$nextTick(() => {
				event?.target?.select();
			});
		},
		handleBlur(event) {
			// Check if we need to correct a negative value
			if (!this.allowNegative && !this.treatAsCredit) {
				const inputElement = event?.target;
				const inputValue = inputElement?.value;

				// Check if the actual input field contains a negative sign or if prop is negative
				const hasNegativeInDOM = inputValue && inputValue.includes('-');
				const propIsNegative = typeof this.value === 'number' && this.value < 0;

				if (hasNegativeInDOM || propIsNegative) {
					// Parse the DOM value to get the actual number
					let correctedValue;
					if (hasNegativeInDOM) {
						const cleanValue = inputValue.replace(/[^\d.]/g, '');
						correctedValue = parseFloat(cleanValue);
						if (isNaN(correctedValue)) {
							correctedValue = 0;
						}
					} else {
						correctedValue = Math.abs(this.value);
					}

					// Force Vue to detect a change by emitting null first, then the correct value
					// This is necessary when the corrected value equals the current value (e.g., 5.99 -> 5.99)
					// Without this, Vue won't update the DOM because it sees no change
					if (correctedValue === this.value) {
						this.$emit("input", null);
					}

					// Emit the corrected value
					this.$nextTick(() => {
						this.$emit("input", correctedValue);

						// Delay setting isInputActive to false until AFTER Vue processes both emits
						this.$nextTick(() => {
							this.isInputActive = false;
						});
					});
				} else {
					// No correction needed, just mark as inactive
					this.isInputActive = false;
				}
			} else {
				// Negatives are allowed or this is a credit field, just mark as inactive
				this.isInputActive = false;
			}

			// Keep tooltip visible for 1.5 seconds so user can read it, then clear
			if (this.attemptedNegative) {
				setTimeout(() => {
					this.attemptedNegative = false;
				}, 1500);
			}
		}
	}
};
</script>

<style scoped>
::v-deep .v-input__slot {
    padding-right: 0 !important;
}

/* Right-align the input text */
::v-deep .v-text-field__slot input {
	text-align: right;
    padding-right: 0.8rem;
}

/* Apply warning border for manual override on outlined fields */
.manual-override::v-deep fieldset {
	border: 2px solid #fb8c00 !important;
	border-color: #fb8c00 !important;
}

/* Ensure border shows in all states */
.manual-override::v-deep .v-input__slot fieldset {
	border: 2px solid #fb8c00 !important;
	border-color: #fb8c00 !important;
}

/* Override default focus and hover states */
.manual-override.v-input--is-focused::v-deep fieldset,
.manual-override:hover::v-deep fieldset {
	border: 2px solid #fb8c00 !important;
	border-color: #fb8c00 !important;
}

/* Additional styling for the manual override state - subtle background */
.manual-override::v-deep .v-input__slot {
	background-color: rgba(251, 140, 0, 0.03) !important;
}

/* Ensure the border color persists even with Vuetify's theme colors */
.manual-override.primary--text::v-deep fieldset {
	border-color: #fb8c00 !important;
}

/* Negative amount styling */
.negative-amount::v-deep .v-text-field__slot input {
	color: #d32f2f !important;
	font-weight: 500;
}

/* Red dollar sign icon for negative amounts */
.negative-amount::v-deep .v-input__prepend-inner .v-icon {
	color: #d32f2f !important;
}

/* Subtle background tint for negative amounts */
.negative-amount::v-deep .v-input__slot {
	background-color: rgba(211, 47, 47, 0.04) !important;
}
</style>
